import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { PlayerVerticalSliceView } from '@aztec-realms/shared-types';

import {
  loadVerticalSlice,
  submitVerticalSliceCommand,
  type VerticalSliceCommand,
} from './vertical-slice';
import { isAuthenticationRequired } from './browser-api';
import { saveOfflineRealmSnapshot } from './offline-realm';
import {
  tribeAssistanceQueryKey,
  tribeExpeditionQueryKey,
  tribeSocialQueryKey,
} from './tribe-query-keys';

export const verticalSliceQueryKey = ['player', 'vertical-slice', 'v1'] as const;

export function constructionRefreshInterval(
  snapshot: PlayerVerticalSliceView | undefined,
  now = Date.now(),
): number | false {
  if (snapshot?.source !== 'api' || snapshot.settlement.construction.length === 0) return false;
  const deadlines = snapshot.settlement.construction
    .map((job) => Date.parse(job.completesAt ?? ''))
    .filter(Number.isFinite);
  if (deadlines.length === 0) return 60_000;
  // At most six foreground refreshes per minute near completion, one per minute otherwise.
  // Completion still comes from the server; never finalize a build from this clock.
  return Math.min(60_000, Math.max(10_000, Math.min(...deadlines) - now + 1000));
}

export function useVerticalSliceQuery({
  enabled = true,
  refreshConstruction = false,
}: { enabled?: boolean; refreshConstruction?: boolean } = {}) {
  const query = useQuery({
    enabled,
    queryKey: verticalSliceQueryKey,
    queryFn: ({ signal }) => loadVerticalSlice(signal),
    refetchOnReconnect: 'always',
    refetchInterval: refreshConstruction
      ? (query) => constructionRefreshInterval(query.state.data)
      : false,
    refetchIntervalInBackground: false,
    retry: (failureCount, error) => !isAuthenticationRequired(error) && failureCount < 1,
  });
  useEffect(() => {
    if (query.data) void saveOfflineRealmSnapshot(query.data).catch(() => undefined);
  }, [query.data]);
  return query;
}

export function useVerticalSliceCommand() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (command: VerticalSliceCommand) => {
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        throw new Error('The realm is read-only offline. Reconnect before making changes.');
      }
      const current = queryClient.getQueryData<PlayerVerticalSliceView>(verticalSliceQueryKey);
      if (!current) throw new Error('The realm must finish loading before it can change.');
      return submitVerticalSliceCommand(current, command);
    },
    onSuccess: async (next) => {
      queryClient.setQueryData(verticalSliceQueryKey, next);
      void saveOfflineRealmSnapshot(next).catch(() => undefined);
      if (next.source === 'api' && next.profile.userId) {
        await queryClient.invalidateQueries({
          queryKey: ['player', 'personal-progress', next.profile.userId],
        });
      }
      if (next.source === 'api' && next.tribe.current) {
        // A confirmed donation/Decode/build can qualify for a Civic lane immediately.
        // Refetch only active observers; other screens merely mark this Tribe stale.
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: tribeExpeditionQueryKey(next.tribe.current.id),
            exact: true,
          }),
          queryClient.invalidateQueries({
            queryKey: tribeAssistanceQueryKey(next.tribe.current.id),
            exact: true,
          }),
          queryClient.invalidateQueries({
            queryKey: tribeSocialQueryKey(next.tribe.current.id),
            exact: true,
          }),
        ]);
      }
    },
  });
}

let commandSequence = 0;

function secureCommandSuffix(): string | undefined {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  if (!globalThis.crypto?.getRandomValues) return undefined;
  const bytes = globalThis.crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
}

export function commandId(scope: string): string {
  commandSequence += 1;
  const uniqueSuffix =
    secureCommandSuffix() ??
    `${Date.now().toString(36)}-${commandSequence.toString(36)}-${Math.random().toString(36).slice(2)}`;
  return `${scope}:${uniqueSuffix}`;
}
